import { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import { LinearProgress, Chip, Box, Card, CardContent } from '@mui/material';
import { Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import { WEBSOCKET_URL } from "../../../config";
import RTKServiceApi from './service';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import ReplayCircleFilledIcon from '@mui/icons-material/ReplayCircleFilled';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import RouterIcon from '@mui/icons-material/Router';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import MemoryIcon from '@mui/icons-material/Memory';
import RadioIcon from '@mui/icons-material/Radio';
import BytesDisplay from '../../../components/BytesDisplay';

function fancyTimeFormat(duration) {
  // For long-running services (years), show days instead of just hours
  const days = ~~(duration / 86400);
  const hrs = ~~((duration % 86400) / 3600);
  const mins = ~~((duration % 3600) / 60);
  const secs = ~~duration % 60;

  let ret = "";

  if (days > 0) {
    ret += days + "d " + hrs + "h " + mins + "m";
  } else if (hrs > 0) {
    ret += hrs + ":" + (mins < 10 ? "0" : "") + mins + ":" + (secs < 10 ? "0" : "") + secs;
  } else {
    ret += mins + ":" + (secs < 10 ? "0" : "") + secs;
  }

  return ret;
}

const rtkservice = new RTKServiceApi();

export default function RTKServiceStatusWidget(props) {
  let navigate = useNavigate();
  const id = props.id;
  const StatusWebSocket = useRef(null);

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState();
  const [status, setStatus] = useState({});

  const [oldBytesStreamed, setOldBytesStreamed] = useState(0);
  const [oldBytesRead, setOldBytesRead] = useState(0);
  const [progress, setProgress] = useState(0);
  const [incrementStreamed, setIncrementStreamed] = useState(0);
  const [incrementRead, setIncrementRead] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  function fetchData() {
    setLoading(true);
     rtkservice.get(id).then(data => {
      setSettings(data);
      setLoading(false);
    });
  }

  useEffect(() => {
    // Clamp progress to prevent overflow in long-running services
    if(progress > 100 ) setProgress(0);
  }, [progress]);

  useEffect(() => {
    if(status.hasOwnProperty('bytes_streamed') && status.bytes_streamed) {
      if(oldBytesStreamed === 0) {
        setIncrementStreamed(0);
      }
      else {
        setIncrementStreamed((Number(status.bytes_streamed) - Number(oldBytesStreamed)) / 100);
      }
      // Clamp progress increment to prevent accumulation errors
      setProgress((prev) => Math.min(prev + incrementStreamed, 100));
      setOldBytesStreamed(status.bytes_streamed);
    }
  }, [status.bytes_streamed]);

  useEffect(() => {
    if(status.hasOwnProperty('bytes_read') && status.bytes_read) {
      if(oldBytesRead === 0) {
        setIncrementRead(0);
      }
      else {
        setIncrementRead((Number(status.bytes_read) - Number(oldBytesRead)) / 100);
      }
      setOldBytesRead(status.bytes_read);
    }
  }, [status.bytes_read]);

  useEffect(() => {
    let reconnectTimeout;

    const connectStatusWebSocket = () => {
      StatusWebSocket.current = new WebSocket(
        WEBSOCKET_URL + '/ws/gnss/' + id + '/');

      StatusWebSocket.current.onclose = function (e) {
        reconnectTimeout = setTimeout(() => {
          connectStatusWebSocket();
        }, 1000);
      };

      StatusWebSocket.current.onerror = function (error) {
        // WebSocket error - connection will close and trigger reconnect
      };

      StatusWebSocket.current.onmessage = function (e) {
        try {
          const data = JSON.parse(e.data);
          setStatus(data);
        }
        catch (error) {
          console.error('Error parsing status WebSocket data:', error);
        }
      };
    };

    connectStatusWebSocket();

    return () => {
      clearTimeout(reconnectTimeout);
      if (StatusWebSocket.current) {
        StatusWebSocket.current.close();
      }
    };
  }, [id]);

  if (loading) {
    return <LinearProgress />
  }

  const getStatusIcon = () => {
    if (status.status === 'starting') return <PendingIcon color="warning" />;
    if (status.running) return <CheckCircleIcon color="success" />;
    return <ErrorIcon color="error" />;
  };

  const getStatusChip = () => {
    if (status.status === 'starting') return <Chip label="Starting" color="warning" size="small" />;
    if (status.running) return <Chip label="Running" color="success" size="small" />;
    return <Chip label="Stopped" color="error" size="small" />;
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      {/* Header Section */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {getStatusIcon()}
          <Box>
            <Typography variant="h6" component="div">
              {settings.mountpoint}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {settings.path} • Service ID: {id}
            </Typography>
          </Box>
          {getStatusChip()}
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            color={status.running || status.status === 'starting' ? "error" : "success"}
            startIcon={status.running || status.status === 'starting' ? <StopCircleIcon /> : <PlayCircleFilledIcon />}
            disabled={status.status === 'starting'}
            onClick={() => {
              if (status.running || status.status === 'starting') {
                rtkservice.stop(settings.id).then(() => {
                  setOldBytesStreamed(0);
                  setOldBytesRead(0);
                }).catch(error => {
                  console.error('Error stopping service:', error);
                });
              } else {
                rtkservice.start(settings.id).then(() => {
                  setOldBytesStreamed(0);
                  setOldBytesRead(0);
                }).catch(error => {
                  console.error('Error starting service:', error);
                });
              }
            }}
          >
            {status.running || status.status === 'starting' ? 'Stop' : 'Start'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReplayCircleFilledIcon />}
            disabled={status.status === 'starting'}
            onClick={() => {
              rtkservice.sync(settings.id).catch(error => {
                console.error('Error restarting service:', error);
              });
            }}
          >
            Restart
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => navigate("/gnss/rtk/" + id + "/edit")}
          >
            Settings
          </Button>
        </Stack>
      </Stack>

      {/* Progress Bar */}
      <Box sx={{ mb: 2 }}>
        {status.status === 'starting' ? (
          <LinearProgress variant="indeterminate" color="warning" sx={{ height: 6, borderRadius: 1 }} />
        ) : status.running ? (
          <LinearProgress variant="determinate" value={progress} color="success" sx={{ height: 6, borderRadius: 1 }} />
        ) : (
          <LinearProgress variant="determinate" value={1} color="error" sx={{ height: 6, borderRadius: 1 }} />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {status.message}
        </Typography>
      </Box>

      {/* Key Metrics Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">Runtime</Typography>
              <Typography variant="h6" color={status.running ? "success.main" : "error.main"} sx={{ mt: 0.5 }}>
                {fancyTimeFormat(status.run_time_secs || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">From Device</Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                <BytesDisplay bytes={status.bytes_read || 0} />
              </Typography>
              {incrementRead > 0 && (
                <Typography variant="caption" color="success.main">
                  +<BytesDisplay bytes={incrementRead} />/s
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">To Caster</Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                <BytesDisplay bytes={status.bytes_streamed || 0} />
              </Typography>
              {incrementStreamed > 0 && (
                <Typography variant="caption" color="success.main">
                  +<BytesDisplay bytes={incrementStreamed} />/s
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        {settings.radio_enabled && (
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary">To Radio</Typography>
                <Typography variant="h6" sx={{ mt: 0.5 }}>
                  <BytesDisplay bytes={status.radio_bytes_sent || 0} />
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Details Grid */}
      <Grid container spacing={2}>
        {/* NTRIP Caster */}
        <Grid item xs={12} md={6} lg={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <RouterIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2">NTRIP Caster</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body2">Server: {status.ntrip?.server || "-"}</Typography>
                <Typography variant="body2">Port: {status.ntrip?.port || "-"}</Typography>
                <Typography variant="body2">Mountpoint: {status.ntrip?.mountpoint || "-"}</Typography>
                <Typography variant="body2">User: {status.ntrip?.user || "-"}</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Reference Coordinate */}
        <Grid item xs={12} md={6} lg={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <GpsFixedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2">Reference Coordinate</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body2">Lat: {status.gnss?.coordinate?.latitude || "-"}</Typography>
                <Typography variant="body2">Lon: {status.gnss?.coordinate?.longitude || "-"}</Typography>
                <Typography variant="body2">Alt: {status.gnss?.coordinate?.altitude || "-"} m</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Broadcast Settings */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <SignalCellularAltIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2">Broadcast Settings</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body2">Protocol: {settings.protocol}</Typography>
                <Typography variant="body2">Broadcast ID: {status.gnss?.broadcastid !== undefined && status.gnss?.broadcastid !== null ? status.gnss.broadcastid : "-"}</Typography>
                {settings.protocol === "RTCM3" && (
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', wordBreak: 'break-word' }}>
                    RTCM Messages: {status.gnss?.rtcm_messages || "-"}
                  </Typography>
                )}
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {settings.gps && <Chip label="GPS" size="small" color="primary" />}
                  {settings.glonass && <Chip label="GLONASS" size="small" color="success" />}
                  {settings.galileo && <Chip label="Galileo" size="small" color="error" />}
                  {settings.beidou && <Chip label="BeiDou" size="small" color="warning" />}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* GNSS Receiver Info */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <MemoryIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2">GNSS Receiver</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body2">Port: {status.gnss?.path || settings.path || "-"}</Typography>
                <Typography variant="body2">Baudrate: {status.gnss?.baudrate || "-"}</Typography>
                <Typography variant="body2">S/N: {status.gnss?.serialnumber || "-"}</Typography>
                <Typography variant="body2">Firmware: {status.gnss?.firmwareversion || "-"}</Typography>
                <Typography variant="body2">Hardware: {status.gnss?.hardwareversion || "-"}</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Radio Info */}
        {settings.radio_enabled && (
          <Grid item xs={12} md={6} lg={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <RadioIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2">Radio Broadcast</Typography>
                </Stack>
                {status.radio ? (
                  <Stack spacing={0.5}>
                    <Typography variant="body2">Model: {status.radio.model || "-"}</Typography>
                    <Typography variant="body2">Frequency: {status.radio.frequency || "-"}</Typography>
                    <Typography variant="body2">TX Power: {status.radio.txpower || "-"} W</Typography>
                    <Typography variant="body2">Protocol: {status.radio.protocol || "-"}</Typography>
                    <Typography variant="body2">S/N: {status.radio.serialnumber || "-"}</Typography>
                    <Typography variant="body2">Firmware: {status.radio.firmwareversion || "-"}</Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">No radio data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}
